/**
 * activeWorkspace.ts — module-level mirror of the resolved active workspace
 * accountId (multi-org plan D3/D4, 2026-08-16).
 *
 * Published once per account resolution by the chat mount (the same spot
 * that publishes setGroupThreadReadAccount, and for the same reason: the
 * repository layer has no accountId in scope). Consumed by
 * chatThreadIndexRepository to stamp a thread's IMMUTABLE workspace binding
 * (`accountId` + deterministic `sessionId`) at CREATE time only —
 * "threads are born into an organization and stay there."
 *
 * Module-level cache by design: workspace switches do a hard navigation
 * (the established convention), so this value is stable for the lifetime of
 * a loaded page. Null means "not yet resolved" — creates simply omit the
 * stamp and the server-side backfill (session/registry-derived) covers it.
 *
 * INCIDENT-20260818 (org thread bleed): "creates simply omit the stamp"
 * turned out to be a PERMANENT hole, not a transient one — a thread created
 * in the seconds before the migration gate resolves goes out unbound, the
 * create-only discipline means no later client write ever stamps it, and the
 * M4 backfill self-terminates after its full pass, so nothing server-side
 * heals the trickle either. Unbound threads render in EVERY workspace
 * (filterThreadsForWorkspace fails open by design), which the owner observed
 * as organizations sharing one thread list. The resolution listener below
 * lets the repository re-stamp same-page-load creates the moment the
 * workspace resolves — closing the race without weakening the immutability
 * rule (absent → value is the only transition the rules and this path allow).
 */

type ActiveWorkspaceResolvedListener = (accountId: string) => void;

let activeWorkspaceAccountId: string | null = null;
const resolvedListeners = new Set<ActiveWorkspaceResolvedListener>();
const settledWithoutAccountListeners = new Set<() => void>();

export function setActiveWorkspaceAccount(accountId: string | null | undefined): void {
  const next = typeof accountId === 'string' ? accountId.trim() : '';
  const value = next.length > 0 ? next : null;
  const changed = value !== activeWorkspaceAccountId;
  activeWorkspaceAccountId = value;
  if (value && changed) {
    for (const listener of Array.from(resolvedListeners)) {
      try {
        listener(value);
      } catch {
        /* a listener failure must never break the publish */
      }
    }
  }
}

export function getActiveWorkspaceAccount(): string | null {
  return activeWorkspaceAccountId;
}

/**
 * Subscribe to the moment a workspace accountId becomes resolved (null →
 * value, or a value change). Returns an unsubscribe. If a workspace is
 * ALREADY resolved at subscribe time the listener is NOT replayed — callers
 * that need the current value read getActiveWorkspaceAccount() first.
 */
export function onActiveWorkspaceResolved(
  listener: ActiveWorkspaceResolvedListener,
): () => void {
  resolvedListeners.add(listener);
  return () => {
    resolvedListeners.delete(listener);
  };
}

/**
 * The migration gate SETTLED and no workspace will be keyed this page load
 * (genuine guest, or an authed-but-unmigrated account). Deliberately a
 * separate channel from setActiveWorkspaceAccount: publishing null through
 * the resolved listeners would fire consumers that assume a non-null
 * accountId (create re-stamping, deferred flushes). The boot-continuation
 * overlay listens here so a no-workspace settle releases it promptly instead
 * of riding out its watchdog (the 2026-08-20 signed-out 12s hang).
 */
export function publishWorkspaceSettledWithoutAccount(): void {
  for (const listener of Array.from(settledWithoutAccountListeners)) {
    try {
      listener();
    } catch {
      /* a listener failure must never break the publish */
    }
  }
}

export function onWorkspaceSettledWithoutAccount(listener: () => void): () => void {
  settledWithoutAccountListeners.add(listener);
  return () => {
    settledWithoutAccountListeners.delete(listener);
  };
}
